import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, openAuthedFile } from "@/api/client";
import { enumLabel } from "@/i18n/enumLabels";
import {
  DOCUMENT_KIND_LABELS,
  DOCUMENT_AUDIENCE_LABELS,
  DOCUMENT_STATUS_LABELS,
  type DocumentKind,
  type DocumentAudience,
  type DocumentStatus,
  DOCUMENT_KINDS,
  DOCUMENT_STATUSES,
  DOCUMENT_AUDIENCES,
} from "@ckgo2/shared/types";
import { formatDate } from "@/lib/format";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel, { type FacetBucket } from "@/components/common/FacetPanel";
import SignStatusBadge from "@/components/common/SignStatusBadge";
import { FileText, Download, Loader2 } from "lucide-react";

interface DocRow {
  id: string;
  title: string;
  number?: string | null;
  orderId?: string | null;
  kind: DocumentKind;
  audience: DocumentAudience;
  status: DocumentStatus;
  requiresSignature: boolean;
  r2Key?: string | null;
  createdAt: string;
  signedAt?: string | null;
  orderNumber?: string | null;
  customerFirstName?: string | null;
  customerLastName?: string | null;
}

interface FacetedDocs {
  data: DocRow[];
  total: number;
  limit: number;
  facets: Record<string, FacetBucket[]>;
  success: true;
}

export default function DocumentsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const qs = searchParams.toString();
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);

  // "Contracts" quick-filter: the contract register is the kind people reach for, so promote it to a
  // one-click preset on top of the generic Kind facet (which still offers every kind).
  const kindFilter = searchParams.get("kind");
  const setKindPreset = (kind: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (kind) next.set("kind", kind);
    else next.delete("kind");
    next.delete("page");
    setSearchParams(next);
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["documents", qs],
    queryFn: () => api.get<FacetedDocs>(`/api/admin/documents?${qs}`),
  });

  const rows = data?.data ?? [];
  const facets = data?.facets ?? {};

  const openPdf = async (id: string) => {
    setPdfBusy(id);
    try {
      await openAuthedFile(`/api/admin/documents/${id}/pdf`);
    } finally {
      setPdfBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t("opsAdmin.documents.title")}
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">{t("opsAdmin.documents.subtitle")}</p>
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => setKindPreset(null)}
            className={`rounded-full px-3 py-1 text-sm font-medium ${
              !kindFilter
                ? "bg-primary-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            {t("opsAdmin.documents.allDocuments")}
          </button>
          <button
            onClick={() => setKindPreset("contract")}
            className={`rounded-full px-3 py-1 text-sm font-medium ${
              kindFilter === "contract"
                ? "bg-primary-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            {t("opsAdmin.documents.contracts")}
          </button>
        </div>
      </div>

      <FacetedListLayout
        searchPlaceholder={t("opsAdmin.documents.searchPlaceholder")}
        total={data?.total ?? 0}
        limit={data?.limit ?? 25}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={
          <>
            <FacetPanel
              title={t("opsAdmin.common.kind")}
              paramKey="kind"
              order={{ enum: DOCUMENT_KINDS }}
              buckets={facets.kind ?? []}
              labelFor={(v) => enumLabel(DOCUMENT_KIND_LABELS, v as DocumentKind)}
            />
            <FacetPanel
              title={t("common.fields.status")}
              paramKey="status"
              order={{ enum: DOCUMENT_STATUSES }}
              buckets={facets.status ?? []}
              labelFor={(v) => enumLabel(DOCUMENT_STATUS_LABELS, v as DocumentStatus)}
            />
            <FacetPanel
              title={t("opsAdmin.documents.audience")}
              paramKey="audience"
              order={{ enum: DOCUMENT_AUDIENCES }}
              buckets={facets.audience ?? []}
              labelFor={(v) => enumLabel(DOCUMENT_AUDIENCE_LABELS, v as DocumentAudience)}
            />
          </>
        }
      >
        <div className="card overflow-hidden p-0">
          {rows.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-gray-500">
              <FileText className="mb-2 h-10 w-10 text-gray-300" />
              <p>{t("opsAdmin.documents.empty")}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  {[
                    t("opsAdmin.documents.colDocument"),
                    t("opsAdmin.common.order"),
                    t("common.fields.customer"),
                    t("opsAdmin.common.kind"),
                    t("common.fields.status"),
                    t("common.fields.created"),
                    t("opsAdmin.documents.colSigned"),
                    "",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {r.number ? <span className="font-mono">{r.number}</span> : r.title}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {r.orderId ? (
                        <Link
                          to={`/orders/${r.orderId}`}
                          className="text-primary-600 dark:text-primary-400 font-mono hover:underline"
                        >
                          {r.orderNumber ?? "—"}
                        </Link>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {[r.customerFirstName, r.customerLastName].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {enumLabel(DOCUMENT_KIND_LABELS, r.kind)}
                    </td>
                    <td className="px-4 py-3">
                      <SignStatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatDate(r.createdAt)}</td>
                    <td className="px-4 py-3 text-xs">
                      {r.signedAt ? (
                        <span className="text-emerald-600 dark:text-emerald-400">
                          {formatDate(r.signedAt)}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openPdf(r.id)}
                        className="btn-ghost btn-sm"
                        disabled={pdfBusy === r.id || !r.r2Key}
                        title={t("opsAdmin.documents.openPdf")}
                      >
                        {pdfBusy === r.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}
